/**
 * ConversionTab Component
 *
 * Complete conversion management UI for CollaborationDetails page.
 * Handles:
 * - Conversion submission form (for collaborators)
 * - Document uploads with required validation
 * - Submission review (for owners)
 * - Approval/rejection/clarification workflow
 * - Payment status display
 *
 * Module 7: Lead Conversion & Referral Fee
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { Avatar } from 'primereact/avatar';
import { Timeline } from 'primereact/timeline';
import { confirmDialog } from 'primereact/confirmdialog';
import { Chip } from 'primereact/chip';
import { ProgressSpinner } from 'primereact/progressspinner';

import {
    conversionService,
    CONVERSION_STATUS,
    PAYMENT_STATUS,
    DOCUMENT_TYPES,
    REQUIRED_DOCUMENT_TYPES,
    REJECTION_REASONS,
    getStatusConfig,
    getPaymentStatusConfig,
    getDocumentTypeConfig,
    getRejectionReasonConfig,
    checkRequiredDocuments,
    formatCurrency,
    formatLargeAmount,
    formatPercentage,
    formatDate,
    formatDateTime,
    formatTimeAgo,
    formatFileSize,
    getFileIcon,
    isFileViewable,
    calculateReferralFee,
    canPerformAction,
    getConversionTimeline
} from '../../services/conversionService';
import axiosInstance from '../../config/axios.config';

const ConversionTab = ({
    collaborationId,
    collaboration,
    isOwner,
    isCollaborator,
    onRefresh,
    toast
}) => {
    // State
    const [loading, setLoading] = useState(true);
    const [submission, setSubmission] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Form state for creating/editing submission
    const [formData, setFormData] = useState({
        loan_amount_disbursed: null,
        commission_received: null,
        disbursement_date: null,
        lender_name: '',
        lender_branch: '',
        loan_account_number: '',
        sales_manager_name: ''
    });

    // Document upload state
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [downloadingDocId, setDownloadingDocId] = useState(null);
    const [documentType, setDocumentType] = useState(null);
    const [documentLabel, setDocumentLabel] = useState('');
    const fileUploadRef = useRef(null);

    // Dialog states
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [showClarificationDialog, setShowClarificationDialog] = useState(false);
    const [showRespondDialog, setShowRespondDialog] = useState(false);

    // Dialog data
    const [rejectData, setRejectData] = useState({ reason: null, notes: '' });
    const [clarificationNotes, setClarificationNotes] = useState('');
    const [clarificationResponse, setClarificationResponse] = useState('');

    // Calculate owner percentage from collaboration data
    // Priority: 1) commission_details.distributions 2) Calculate from interest_summary.final_percentage
    const getOwnerPercentage = () => {
        // First try to get from commission_details distributions
        const distributionPercentage = collaboration?.commission_details?.distributions?.find(d => d.role === 'owner')?.percentage;
        if (distributionPercentage !== undefined && distributionPercentage !== null && distributionPercentage !== 100) {
            return distributionPercentage;
        }

        // If owner shows 100% or not found, try to calculate from interest_summary
        // interest_summary.final_percentage is the COLLABORATOR's percentage from negotiation
        const finalPercentage = collaboration?.interest_summary?.final_percentage;
        if (finalPercentage !== undefined && finalPercentage !== null) {
            return 100 - finalPercentage;
        }

        // Fallback to distribution percentage or 0
        return distributionPercentage ?? 0;
    };

    const ownerPercentage = getOwnerPercentage();

    // Computed values
    const referralFeeAmount = calculateReferralFee(
        formData.loan_amount_disbursed || submission?.loan_amount_disbursed || 0,
        ownerPercentage
    );

    // Determine if user is the submitter
    const isSubmitter = submission?.submitter?.id === collaboration?.my_profile_id;

    // Load submission data
    useEffect(() => {
        loadSubmission();
    }, [collaborationId]);

    const loadSubmission = async () => {
        try {
            setLoading(true);
            const result = await conversionService.getSubmissionForCollaboration(collaborationId);

            if (result.success && result.data) {
                setSubmission(result.data);

                // Populate form with existing data
                if (result.data) {
                    setFormData({
                        loan_amount_disbursed: result.data.loan_amount_disbursed || null,
                        commission_received: result.data.commission_received || null,
                        disbursement_date: result.data.disbursement_date ? new Date(result.data.disbursement_date) : null,
                        lender_name: result.data.lender_name || '',
                        lender_branch: result.data.lender_branch || '',
                        loan_account_number: result.data.loan_account_number || '',
                        sales_manager_name: result.data.sales_manager_name || ''
                    });
                }
            }
        } catch (error) {
            console.error('Failed to load conversion:', error);
        } finally {
            setLoading(false);
        }
    };

    // Create new submission
    const handleCreateSubmission = async () => {
        if (!formData.loan_amount_disbursed || !formData.commission_received || !formData.disbursement_date) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required Fields',
                detail: 'Please fill in all required fields',
                life: 3000
            });
            return;
        }

        try {
            setProcessing(true);
            const result = await conversionService.createSubmission(collaborationId, {
                ...formData,
                disbursement_date: formData.disbursement_date.toISOString().split('T')[0]
            });

            if (result.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Draft Created',
                    detail: 'Conversion draft created. Upload required documents to submit.',
                    life: 3000
                });
                setSubmission(result.data);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to create conversion',
                life: 4000
            });
        } finally {
            setProcessing(false);
        }
    };

    // Update submission
    const handleUpdateSubmission = async () => {
        try {
            setProcessing(true);
            const result = await conversionService.updateSubmission(submission.id, {
                ...formData,
                disbursement_date: formData.disbursement_date?.toISOString().split('T')[0]
            });

            if (result.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: 'Conversion details updated',
                    life: 3000
                });
                setSubmission(result.data);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to update conversion',
                life: 4000
            });
        } finally {
            setProcessing(false);
        }
    };

    // Submit for approval
    const handleSubmitForApproval = async () => {
        // Check required documents
        const docCheck = checkRequiredDocuments(submission?.documents);
        if (!docCheck.hasAll) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Missing Documents',
                detail: `Please upload: ${docCheck.missingLabels.join(', ')}`,
                life: 4000
            });
            return;
        }

        confirmDialog({
            message: (
                <div>
                    <p className="mb-2">Are you sure you want to submit this conversion for approval?</p>
                    <p className="text-600 text-sm">
                        Referral fee of <strong>{formatCurrency(submission.referral_fee_amount)}</strong> will be
                        deducted from your wallet upon approval.
                    </p>
                </div>
            ),
            header: 'Confirm Submission',
            icon: 'pi pi-send',
            acceptLabel: 'Submit',
            rejectLabel: 'Cancel',
            accept: async () => {
                try {
                    setProcessing(true);
                    const result = await conversionService.submitForApproval(submission.id);

                    if (result.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Submitted',
                            detail: 'Conversion submitted for owner approval',
                            life: 3000
                        });
                        setSubmission(result.data);
                        onRefresh?.();
                    } else {
                        throw new Error(result.message);
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to submit',
                        life: 4000
                    });
                } finally {
                    setProcessing(false);
                }
            }
        });
    };

    // Cancel submission
    const handleCancelSubmission = async () => {
        confirmDialog({
            message: 'Are you sure you want to cancel this conversion submission?',
            header: 'Confirm Cancellation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: 'Cancel Submission',
            rejectLabel: 'Keep',
            accept: async () => {
                try {
                    setProcessing(true);
                    const result = await conversionService.cancelSubmission(submission.id);

                    if (result.success) {
                        toast.current?.show({
                            severity: 'info',
                            summary: 'Cancelled',
                            detail: 'Conversion submission cancelled',
                            life: 3000
                        });
                        setSubmission(null);
                        setFormData({
                            loan_amount_disbursed: null,
                            commission_received: null,
                            disbursement_date: null,
                            lender_name: '',
                            lender_branch: '',
                            loan_account_number: '',
                            sales_manager_name: ''
                        });
                        onRefresh?.();
                    } else {
                        throw new Error(result.message);
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to cancel',
                        life: 4000
                    });
                } finally {
                    setProcessing(false);
                }
            }
        });
    };

    // Document upload
    const handleDocumentUpload = async (event) => {
        const file = event.files[0];
        if (!file || !documentType) return;

        try {
            setUploadingDocument(true);
            const result = await conversionService.uploadDocument(
                submission.id,
                file,
                documentType,
                documentType === 'other' ? documentLabel : null
            );

            if (result.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Uploaded',
                    detail: 'Document uploaded successfully',
                    life: 3000
                });
                await loadSubmission();
                setDocumentType(null);
                setDocumentLabel('');
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Upload Failed',
                detail: error.message || 'Failed to upload document',
                life: 4000
            });
        } finally {
            setUploadingDocument(false);
            fileUploadRef.current?.clear();
        }
    };

    // Delete document
    const handleDeleteDocument = async (documentId) => {
        confirmDialog({
            message: 'Are you sure you want to delete this document?',
            header: 'Confirm Delete',
            icon: 'pi pi-trash',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const result = await conversionService.deleteDocument(submission.id, documentId);

                    if (result.success) {
                        toast.current?.show({
                            severity: 'info',
                            summary: 'Deleted',
                            detail: 'Document deleted',
                            life: 3000
                        });
                        await loadSubmission();
                    } else {
                        throw new Error(result.message);
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to delete',
                        life: 4000
                    });
                }
            }
        });
    };

    // View document in new window with proper viewer
    const handleViewDocument = (doc) => {
        const fileUrl = doc.url;
        if (!fileUrl) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Document URL not available',
                life: 3000
            });
            return;
        }

        const documentTitle = doc.name || doc.document_name || 'Document';
        const fileExtension = (doc.file_name || doc.name || '').split('.').pop()?.toLowerCase();
        const isPDF = fileExtension === 'pdf';
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension);

        const newWindow = window.open('', '_blank', 'width=1000,height=800');
        if (newWindow) {
            newWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>${documentTitle} - Document Viewer</title>
                    <style>
                        body { margin: 0; padding: 20px; font-family: Arial, sans-serif; background: #f5f5f5; }
                        .header { background: white; padding: 1rem; margin-bottom: 1rem; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); display: flex; justify-content: space-between; align-items: center; }
                        .header h3 { margin: 0; }
                        .document-container { background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
                        iframe, img { width: 100%; height: 80vh; border: none; }
                        img { object-fit: contain; background: white; }
                        .actions { display: flex; gap: 10px; }
                        .btn { padding: 8px 16px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer; text-decoration: none; font-size: 14px; }
                        .btn:hover { background: #2563eb; }
                        .btn-secondary { background: #6b7280; }
                        .btn-secondary:hover { background: #4b5563; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h3>${documentTitle}</h3>
                        <div class="actions">
                            <a href="${fileUrl}" download="${doc.name || doc.document_name || 'document'}" class="btn">Download</a>
                            <button class="btn btn-secondary" onclick="window.close()">Close</button>
                        </div>
                    </div>
                    <div class="document-container">
                        ${isPDF ?
                            `<iframe src="${fileUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH" title="${documentTitle}"></iframe>` :
                            isImage ?
                            `<img src="${fileUrl}" alt="${documentTitle}" />` :
                            `<iframe src="${fileUrl}" title="${documentTitle}"></iframe>`
                        }
                    </div>
                </body>
                </html>
            `);
            newWindow.document.close();
        } else {
            // Fallback if popup blocked
            window.open(fileUrl, '_blank');
        }
    };

    // Download document using API endpoint
    const handleDownloadDocument = async (doc) => {
        if (!doc.id || !submission?.id) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Document not available',
                life: 3000
            });
            return;
        }

        setDownloadingDocId(doc.id);
        try {
            const response = await axiosInstance.get(
                `/leadflow/conversions/${submission.id}/documents/${doc.id}/download`,
                { responseType: 'blob' }
            );

            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = doc.name || doc.document_name || 'document';
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();

            // Cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }, 100);

            toast.current?.show({
                severity: 'success',
                summary: 'Download Complete',
                detail: `${doc.name || doc.document_name} downloaded successfully`,
                life: 3000
            });
        } catch (error) {
            console.error('Download error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Download Failed',
                detail: 'Unable to download document. Please try again.',
                life: 4000
            });
        } finally {
            setDownloadingDocId(null);
        }
    };

    // Owner actions
    const handleApprove = async () => {
        confirmDialog({
            message: (
                <div>
                    <p className="mb-2">Are you sure you want to approve this conversion?</p>
                    <p className="text-600 text-sm">
                        <strong>{formatCurrency(submission.referral_fee_amount)}</strong> referral fee will be
                        transferred from the collaborator's wallet to your wallet.
                    </p>
                </div>
            ),
            header: 'Approve Conversion',
            icon: 'pi pi-check-circle',
            acceptClassName: 'p-button-success',
            acceptLabel: 'Approve',
            rejectLabel: 'Cancel',
            accept: async () => {
                try {
                    setProcessing(true);
                    const result = await conversionService.approveConversion(submission.id);

                    if (result.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Approved',
                            detail: result.message,
                            life: 4000
                        });
                        setSubmission(result.data?.submission || result.data);
                        onRefresh?.();
                    } else {
                        throw new Error(result.message);
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to approve',
                        life: 4000
                    });
                } finally {
                    setProcessing(false);
                }
            }
        });
    };

    const handleReject = async () => {
        if (!rejectData.reason) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please select a rejection reason',
                life: 3000
            });
            return;
        }

        try {
            setProcessing(true);
            const result = await conversionService.rejectConversion(
                submission.id,
                rejectData.reason,
                rejectData.notes
            );

            if (result.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Rejected',
                    detail: 'Conversion has been rejected',
                    life: 3000
                });
                setSubmission(result.data);
                setShowRejectDialog(false);
                setRejectData({ reason: null, notes: '' });
                onRefresh?.();
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to reject',
                life: 4000
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleRequestClarification = async () => {
        if (!clarificationNotes.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please specify what clarification is needed',
                life: 3000
            });
            return;
        }

        try {
            setProcessing(true);
            const result = await conversionService.requestClarification(submission.id, clarificationNotes);

            if (result.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Request Sent',
                    detail: 'Clarification request sent to collaborator',
                    life: 3000
                });
                setSubmission(result.data);
                setShowClarificationDialog(false);
                setClarificationNotes('');
                onRefresh?.();
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to request clarification',
                life: 4000
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleRespondToClarification = async () => {
        if (!clarificationResponse.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please provide a response',
                life: 3000
            });
            return;
        }

        try {
            setProcessing(true);
            const result = await conversionService.respondToClarification(submission.id, clarificationResponse);

            if (result.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Response Sent',
                    detail: 'Response submitted. Awaiting owner review.',
                    life: 3000
                });
                setSubmission(result.data);
                setShowRespondDialog(false);
                setClarificationResponse('');
                onRefresh?.();
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to respond',
                life: 4000
            });
        } finally {
            setProcessing(false);
        }
    };

    // Render loading state
    if (loading) {
        return (
            <div className="p-3">
                <Skeleton height="2rem" className="mb-2" />
                <Skeleton height="8rem" className="mb-2" />
                <Skeleton height="6rem" />
            </div>
        );
    }

    // Render empty state for collaborators (no submission yet)
    if (!submission && isCollaborator) {
        return (
            <div className="p-3">
                <div className="text-center mb-3">
                    <i className="pi pi-check-circle text-primary mb-2" style={{ fontSize: '2rem' }} />
                    <div className="text-900 font-semibold mb-1" style={{ fontSize: '0.85rem' }}>Submit Conversion</div>
                    <p className="text-600 mb-3" style={{ fontSize: '0.7rem' }}>
                        Report a successful loan conversion to receive your commission.
                        The lead owner will review and approve your submission.
                    </p>
                </div>

                {/* Referral Fee Info */}
                <Message
                    severity="info"
                    className="mb-3 w-full"
                    content={
                        <div style={{ fontSize: '0.7rem' }}>
                            Upon approval, a referral fee of {formatPercentage(ownerPercentage)} of the disbursed loan amount will be paid to the lead owner.
                        </div>
                    }
                />

                {/* Conversion Form */}
                <Card className="mb-3">
                    <div className="font-semibold mb-3" style={{ fontSize: '0.8rem' }}>Conversion Details</div>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>
                                    Loan Amount Disbursed <span className="text-red-500">*</span>
                                </label>
                                <InputNumber
                                    value={formData.loan_amount_disbursed}
                                    onValueChange={(e) => setFormData({ ...formData, loan_amount_disbursed: e.value })}
                                    mode="currency"
                                    currency="INR"
                                    locale="en-IN"
                                    placeholder="Enter amount"
                                    className="w-full"
                                    inputStyle={{ fontSize: '0.7rem' }}
                                />
                                {formData.loan_amount_disbursed > 0 && ownerPercentage > 0 && (
                                    <small className="text-600 block mt-1" style={{ fontSize: '0.6rem' }}>
                                        Referral fee to owner: {formatCurrency(referralFeeAmount)} ({formatPercentage(ownerPercentage)} of disbursed)
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>
                                    Commission Received <span className="text-red-500">*</span>
                                </label>
                                <InputNumber
                                    value={formData.commission_received}
                                    onValueChange={(e) => setFormData({ ...formData, commission_received: e.value })}
                                    mode="currency"
                                    currency="INR"
                                    locale="en-IN"
                                    placeholder="Commission from bank"
                                    className="w-full"
                                    inputStyle={{ fontSize: '0.7rem' }}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>
                                    Disbursement Date <span className="text-red-500">*</span>
                                </label>
                                <Calendar
                                    value={formData.disbursement_date}
                                    onChange={(e) => setFormData({ ...formData, disbursement_date: e.value })}
                                    maxDate={new Date()}
                                    showIcon
                                    dateFormat="dd M yy"
                                    placeholder="Select date"
                                    className="w-full"
                                    inputStyle={{ fontSize: '0.7rem' }}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Lender Name</label>
                                <InputText
                                    value={formData.lender_name}
                                    onChange={(e) => setFormData({ ...formData, lender_name: e.target.value })}
                                    placeholder="Bank/NBFC name"
                                    className="w-full"
                                    style={{ fontSize: '0.7rem' }}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Lender Branch</label>
                                <InputText
                                    value={formData.lender_branch}
                                    onChange={(e) => setFormData({ ...formData, lender_branch: e.target.value })}
                                    placeholder="Branch name"
                                    className="w-full"
                                    style={{ fontSize: '0.7rem' }}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Loan Account Number</label>
                                <InputText
                                    value={formData.loan_account_number}
                                    onChange={(e) => setFormData({ ...formData, loan_account_number: e.target.value })}
                                    placeholder="Account number"
                                    className="w-full"
                                    style={{ fontSize: '0.7rem' }}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Sales Manager Name</label>
                                <InputText
                                    value={formData.sales_manager_name}
                                    onChange={(e) => setFormData({ ...formData, sales_manager_name: e.target.value })}
                                    placeholder="Bank/Lender sales manager"
                                    className="w-full"
                                    style={{ fontSize: '0.7rem' }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-end mt-3">
                        <Button
                            label="Create Draft"
                            icon="pi pi-save"
                            onClick={handleCreateSubmission}
                            loading={processing}
                            style={{ fontSize: '0.7rem' }}
                        />
                    </div>
                </Card>
            </div>
        );
    }

    // Render for owners when no submission exists
    if (!submission && isOwner) {
        return (
            <div className="p-3 text-center">
                <i className="pi pi-hourglass text-500 mb-2" style={{ fontSize: '2rem' }} />
                <div className="text-600 font-semibold mb-1" style={{ fontSize: '0.85rem' }}>Waiting for Conversion</div>
                <p className="text-500" style={{ fontSize: '0.7rem' }}>
                    The collaborator has not yet submitted a conversion for this lead.
                    You will be notified when a conversion is submitted for your approval.
                </p>
            </div>
        );
    }

    // Get status config for current submission
    const statusConfig = getStatusConfig(submission.status);
    const paymentConfig = getPaymentStatusConfig(submission.payment_status);
    const canEdit = canPerformAction(submission, 'edit', isOwner, isSubmitter);
    const canSubmit = canPerformAction(submission, 'submit', isOwner, isSubmitter);
    const canCancel = canPerformAction(submission, 'cancel', isOwner, isSubmitter);
    const canApprove = canPerformAction(submission, 'approve', isOwner, isSubmitter);
    const canReject = canPerformAction(submission, 'reject', isOwner, isSubmitter);
    const canRequestClarification = canPerformAction(submission, 'request_clarification', isOwner, isSubmitter);
    const canRespondClarification = canPerformAction(submission, 'respond_clarification', isOwner, isSubmitter);
    const canUploadDocument = canPerformAction(submission, 'upload_document', isOwner, isSubmitter);

    // Document check
    const docCheck = checkRequiredDocuments(submission.documents);

    return (
        <div className="p-3">
            {/* Status Header */}
            <div className="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div className="flex align-items-center gap-2">
                    <Tag
                        value={statusConfig.label}
                        style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color, fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}
                        icon={`pi ${statusConfig.icon}`}
                    />
                    {submission.status === 'approved' && (
                        <Tag
                            value={paymentConfig.label}
                            style={{ backgroundColor: paymentConfig.bgColor, color: paymentConfig.color, fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}
                            icon={`pi ${paymentConfig.icon}`}
                        />
                    )}
                </div>

                <div className="flex gap-2 flex-wrap">
                    {canCancel && (
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-outlined p-button-secondary"
                            style={{ fontSize: '0.7rem' }}
                            onClick={handleCancelSubmission}
                            loading={processing}
                        />
                    )}
                    {canSubmit && (
                        <Button
                            label="Submit for Approval"
                            icon="pi pi-send"
                            style={{ fontSize: '0.7rem' }}
                            onClick={handleSubmitForApproval}
                            loading={processing}
                            disabled={!docCheck.hasAll}
                        />
                    )}
                    {canApprove && (
                        <Button
                            label="Approve"
                            icon="pi pi-check"
                            className="p-button-success"
                            style={{ fontSize: '0.7rem' }}
                            onClick={handleApprove}
                            loading={processing}
                        />
                    )}
                    {canRequestClarification && (
                        <Button
                            label="Request Clarification"
                            icon="pi pi-question-circle"
                            className="p-button-outlined"
                            style={{ fontSize: '0.7rem' }}
                            onClick={() => setShowClarificationDialog(true)}
                        />
                    )}
                    {canReject && (
                        <Button
                            label="Reject"
                            icon="pi pi-times"
                            className="p-button-danger"
                            style={{ fontSize: '0.7rem' }}
                            onClick={() => setShowRejectDialog(true)}
                        />
                    )}
                    {canRespondClarification && (
                        <Button
                            label="Respond to Clarification"
                            icon="pi pi-reply"
                            className="p-button-info"
                            style={{ fontSize: '0.7rem' }}
                            onClick={() => setShowRespondDialog(true)}
                        />
                    )}
                </div>
            </div>

            {/* Clarification Message */}
            {submission.status === 'clarification_requested' && submission.clarification?.notes && (
                <Message
                    severity="warn"
                    className="mb-3 w-full"
                    content={
                        <div style={{ fontSize: '0.7rem' }}>
                            <strong>Clarification Requested:</strong>
                            <p className="mt-1 mb-0">{submission.clarification.notes}</p>
                        </div>
                    }
                />
            )}

            {/* Rejection Message */}
            {submission.status === 'rejected' && submission.rejection && (
                <Message
                    severity="error"
                    className="mb-3 w-full"
                    content={
                        <div style={{ fontSize: '0.7rem' }}>
                            <strong>Rejection Reason: {getRejectionReasonConfig(submission.rejection.reason).label}</strong>
                            {submission.rejection.notes && (
                                <p className="mt-1 mb-0">{submission.rejection.notes}</p>
                            )}
                        </div>
                    }
                />
            )}

            {/* Clarification History - Show full history even when status changed */}
            {submission.clarification && submission.status !== 'clarification_requested' && (
                <Card className="mb-3">
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-history text-primary" style={{ fontSize: '0.7rem' }} />
                        <span className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>Clarification History</span>
                    </div>
                    <div className="surface-100 border-round p-2">
                        <div className="flex align-items-center gap-2 mb-1">
                            <i className="pi pi-question-circle text-orange-500" style={{ fontSize: '0.7rem' }} />
                            <span className="font-medium text-orange-700" style={{ fontSize: '0.7rem' }}>Owner's Request</span>
                        </div>
                        <div className="ml-4 pl-2 border-left-2 border-orange-300 mb-2">
                            <div className="text-700" style={{ fontSize: '0.7rem' }}>{submission.clarification.notes}</div>
                        </div>

                        {submission.clarification.response && (
                            <>
                                <div className="flex align-items-center gap-2 mb-1">
                                    <i className="pi pi-reply text-green-500" style={{ fontSize: '0.7rem' }} />
                                    <span className="font-medium text-green-700" style={{ fontSize: '0.7rem' }}>Your Response</span>
                                    {submission.clarification.responded_at && (
                                        <span className="text-500" style={{ fontSize: '0.6rem' }}>
                                            ({formatTimeAgo(submission.clarification.responded_at)})
                                        </span>
                                    )}
                                </div>
                                <div className="ml-4 pl-2 border-left-2 border-green-300">
                                    <div className="text-700" style={{ fontSize: '0.7rem' }}>{submission.clarification.response}</div>
                                </div>
                            </>
                        )}
                    </div>
                </Card>
            )}

            {/* Rejection History - Show when there was a previous rejection but status changed */}
            {submission.rejection && submission.status !== 'rejected' && (
                <Card className="mb-3">
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-history text-primary" style={{ fontSize: '0.7rem' }} />
                        <span className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>Previous Rejection</span>
                    </div>
                    <div className="surface-100 border-round p-2">
                        <div className="flex align-items-center gap-2 mb-1">
                            <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.7rem' }} />
                            <span className="font-medium text-red-700" style={{ fontSize: '0.7rem' }}>
                                Reason: {getRejectionReasonConfig(submission.rejection.reason).label}
                            </span>
                        </div>
                        {submission.rejection.notes && (
                            <div className="ml-4 pl-2 border-left-2 border-red-300">
                                <div className="text-600" style={{ fontSize: '0.7rem' }}>{submission.rejection.notes}</div>
                            </div>
                        )}
                    </div>
                </Card>
            )}

            <div className="grid">
                {/* Conversion Details */}
                <div className="col-12 lg:col-8">
                    <Card className="mb-3">
                        <div className="font-semibold mb-3" style={{ fontSize: '0.8rem' }}>Conversion Details</div>
                        {canEdit ? (
                            // Editable form
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Loan Amount Disbursed</label>
                                        <InputNumber
                                            value={formData.loan_amount_disbursed}
                                            onValueChange={(e) => setFormData({ ...formData, loan_amount_disbursed: e.value })}
                                            mode="currency"
                                            currency="INR"
                                            locale="en-IN"
                                            className="w-full"
                                            inputStyle={{ fontSize: '0.7rem' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Commission Received</label>
                                        <InputNumber
                                            value={formData.commission_received}
                                            onValueChange={(e) => setFormData({ ...formData, commission_received: e.value })}
                                            mode="currency"
                                            currency="INR"
                                            locale="en-IN"
                                            className="w-full"
                                            inputStyle={{ fontSize: '0.7rem' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Disbursement Date</label>
                                        <Calendar
                                            value={formData.disbursement_date}
                                            onChange={(e) => setFormData({ ...formData, disbursement_date: e.value })}
                                            maxDate={new Date()}
                                            showIcon
                                            dateFormat="dd M yy"
                                            className="w-full"
                                            inputStyle={{ fontSize: '0.7rem' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Lender Name</label>
                                        <InputText
                                            value={formData.lender_name}
                                            onChange={(e) => setFormData({ ...formData, lender_name: e.target.value })}
                                            className="w-full"
                                            style={{ fontSize: '0.7rem' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Lender Branch</label>
                                        <InputText
                                            value={formData.lender_branch}
                                            onChange={(e) => setFormData({ ...formData, lender_branch: e.target.value })}
                                            className="w-full"
                                            style={{ fontSize: '0.7rem' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Loan Account Number</label>
                                        <InputText
                                            value={formData.loan_account_number}
                                            onChange={(e) => setFormData({ ...formData, loan_account_number: e.target.value })}
                                            className="w-full"
                                            style={{ fontSize: '0.7rem' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Sales Manager Name</label>
                                        <InputText
                                            value={formData.sales_manager_name}
                                            onChange={(e) => setFormData({ ...formData, sales_manager_name: e.target.value })}
                                            className="w-full"
                                            style={{ fontSize: '0.7rem' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 flex justify-content-end">
                                    <Button
                                        label="Save Changes"
                                        icon="pi pi-save"
                                        onClick={handleUpdateSubmission}
                                        loading={processing}
                                        style={{ fontSize: '0.7rem' }}
                                    />
                                </div>
                            </div>
                        ) : (
                            // Read-only view
                            <div className="grid">
                                <div className="col-6 md:col-4">
                                    <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Loan Amount</div>
                                    <div className="text-900 font-semibold" style={{ fontSize: '0.85rem' }}>{formatCurrency(submission.loan_amount_disbursed)}</div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Commission Received</div>
                                    <div className="text-900 font-semibold" style={{ fontSize: '0.85rem' }}>{formatCurrency(submission.commission_received)}</div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Disbursement Date</div>
                                    <div className="text-900 font-semibold" style={{ fontSize: '0.75rem' }}>{formatDate(submission.disbursement_date)}</div>
                                </div>
                                {submission.lender_name && (
                                    <div className="col-6 md:col-4 mt-2">
                                        <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Lender</div>
                                        <div className="text-900" style={{ fontSize: '0.75rem' }}>{submission.lender_name}</div>
                                    </div>
                                )}
                                {submission.lender_branch && (
                                    <div className="col-6 md:col-4 mt-2">
                                        <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Branch</div>
                                        <div className="text-900" style={{ fontSize: '0.75rem' }}>{submission.lender_branch}</div>
                                    </div>
                                )}
                                {submission.loan_account_number && (
                                    <div className="col-6 md:col-4 mt-2">
                                        <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Account Number</div>
                                        <div className="text-900" style={{ fontSize: '0.75rem' }}>{submission.loan_account_number}</div>
                                    </div>
                                )}
                                {submission.sales_manager_name && (
                                    <div className="col-6 md:col-4 mt-2">
                                        <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Sales Manager</div>
                                        <div className="text-900" style={{ fontSize: '0.75rem' }}>{submission.sales_manager_name}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>

                    {/* Documents Section */}
                    <Card className="mb-3">
                        <div className="font-semibold mb-3" style={{ fontSize: '0.8rem' }}>Proof Documents</div>
                        {/* Document status */}
                        {!docCheck.hasAll && canUploadDocument && (
                            <Message
                                severity="warn"
                                className="mb-3 w-full"
                                content={
                                    <div style={{ fontSize: '0.7rem' }}>
                                        Missing required documents: {docCheck.missingLabels.join(', ')}
                                    </div>
                                }
                            />
                        )}

                        {/* Document list */}
                        {submission.documents?.length > 0 ? (
                            <div className="grid">
                                {submission.documents.map((doc) => {
                                    const typeConfig = getDocumentTypeConfig(doc.type || doc.document_type);
                                    return (
                                        <div key={doc.id} className="col-12 md:col-6">
                                            <div className="surface-border border-1 border-round p-2">
                                                <div className="flex align-items-center gap-2 mb-2">
                                                    <i className={`pi ${getFileIcon(doc.mime_type)} text-primary flex-shrink-0`} style={{ fontSize: '1.1rem' }} />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="font-semibold text-900" style={{ fontSize: '0.7rem' }}>{typeConfig.label}</div>
                                                        <div className="text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.65rem' }}>{doc.name || doc.document_name}</div>
                                                        <div className="text-500" style={{ fontSize: '0.6rem' }}>{formatFileSize(doc.file_size)}</div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 border-top-1 surface-border pt-2">
                                                    {doc.url && (
                                                        <Button
                                                            icon="pi pi-eye"
                                                            label="View"
                                                            className="p-button-text p-button-sm flex-1"
                                                            style={{ fontSize: '0.65rem' }}
                                                            onClick={() => handleViewDocument(doc)}
                                                        />
                                                    )}
                                                    <Button
                                                        icon={downloadingDocId === doc.id ? "pi pi-spinner pi-spin" : "pi pi-download"}
                                                        label="Download"
                                                        className="p-button-text p-button-sm flex-1"
                                                        style={{ fontSize: '0.65rem' }}
                                                        disabled={downloadingDocId === doc.id}
                                                        onClick={() => handleDownloadDocument(doc)}
                                                    />
                                                    {canUploadDocument && (
                                                        <Button
                                                            icon="pi pi-trash"
                                                            aria-label="Delete document"
                                                            className="p-button-text p-button-sm p-button-danger"
                                                            tooltip="Delete"
                                                            onClick={() => handleDeleteDocument(doc.id)}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-500 text-center my-3" style={{ fontSize: '0.7rem' }}>No documents uploaded yet.</p>
                        )}

                        {/* Upload form */}
                        {canUploadDocument && (
                            <div className="mt-3 p-2 surface-ground border-round">
                                <div className="font-medium mb-2" style={{ fontSize: '0.75rem' }}>Upload Document</div>
                                <div className="grid">
                                    <div className="col-12 md:col-4">
                                        <Dropdown
                                            value={documentType}
                                            options={Object.values(DOCUMENT_TYPES)}
                                            optionLabel="label"
                                            optionValue="value"
                                            onChange={(e) => setDocumentType(e.value)}
                                            placeholder="Select document type"
                                            className="w-full"
                                            style={{ fontSize: '0.7rem' }}
                                        />
                                    </div>
                                    {documentType === 'other' && (
                                        <div className="col-12 md:col-4">
                                            <InputText
                                                value={documentLabel}
                                                onChange={(e) => setDocumentLabel(e.target.value)}
                                                placeholder="Document description"
                                                className="w-full"
                                                style={{ fontSize: '0.7rem' }}
                                            />
                                        </div>
                                    )}
                                    <div className="col-12 md:col-4">
                                        <FileUpload
                                            ref={fileUploadRef}
                                            mode="basic"
                                            name="file"
                                            accept="application/pdf,image/*"
                                            maxFileSize={10485760}
                                            customUpload
                                            uploadHandler={handleDocumentUpload}
                                            auto
                                            chooseLabel="Choose File"
                                            disabled={!documentType || uploadingDocument}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="col-12 lg:col-4">
                    {/* Referral Fee Card */}
                    <Card className="mb-3">
                        <div className="text-center">
                            <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Referral Fee to Owner</div>
                            <div className="font-bold text-primary mb-1" style={{ fontSize: '1.1rem' }}>
                                {formatCurrency(submission.referral_fee_amount)}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.65rem' }}>
                                {formatPercentage(submission.referral_fee_percentage)} of {formatCurrency(submission.loan_amount_disbursed)}
                            </div>
                        </div>

                        {submission.status === 'approved' && submission.payment_status === 'completed' && (
                            <div className="mt-3 p-2 surface-ground border-round text-center">
                                <i className="pi pi-check-circle text-green-500 mb-1" style={{ fontSize: '1.25rem' }} />
                                <div className="text-green-600 font-semibold" style={{ fontSize: '0.7rem' }}>Payment Completed</div>
                                <div className="text-500" style={{ fontSize: '0.6rem' }}>{formatDateTime(submission.payment_completed_at)}</div>
                            </div>
                        )}
                    </Card>

                    {/* Submitter Info */}
                    {submission.submitter && (
                        <Card className="mb-3">
                            <div className="font-semibold mb-2" style={{ fontSize: '0.75rem' }}>Submitted By</div>
                            <div className="flex align-items-center gap-2">
                                <Avatar
                                    image={submission.submitter.avatar}
                                    label={submission.submitter.name?.charAt(0)}
                                    shape="circle"
                                    className="bg-primary"
                                    style={{ width: '2rem', height: '2rem', fontSize: '0.75rem' }}
                                />
                                <div>
                                    <div className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>{submission.submitter.name}</div>
                                    <div className="text-500" style={{ fontSize: '0.6rem' }}>
                                        {formatTimeAgo(submission.submitted_at || submission.created_at)}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Timeline */}
                    <Card>
                        <div className="font-semibold mb-2" style={{ fontSize: '0.75rem' }}>Timeline</div>
                        <Timeline
                            value={getConversionTimeline(submission)}
                            content={(item) => (
                                <div>
                                    <div className={item.completed ? 'text-900' : 'text-500'} style={{ fontSize: '0.7rem' }}>
                                        {item.label}
                                    </div>
                                    {item.date && (
                                        <div className="text-500" style={{ fontSize: '0.6rem' }}>{formatDateTime(item.date)}</div>
                                    )}
                                </div>
                            )}
                            marker={(item) => (
                                <span className={`flex align-items-center justify-content-center border-circle ${item.completed ? 'bg-primary text-white' : 'surface-200 text-500'}`} style={{ width: '1.5rem', height: '1.5rem' }}>
                                    <i className={`pi ${item.icon}`} style={{ fontSize: '0.6rem' }} />
                                </span>
                            )}
                        />
                    </Card>
                </div>
            </div>

            {/* Reject Dialog */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.8rem' }} />
                        <span style={{ fontSize: '0.85rem' }}>Reject Conversion</span>
                    </div>
                }
                visible={showRejectDialog}
                onHide={() => setShowRejectDialog(false)}
                style={{ width: '450px' }}
                modal
                className="compact-conversion-reject-dialog"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text" style={{ fontSize: '0.7rem' }} onClick={() => setShowRejectDialog(false)} />
                        <Button label="Reject" className="p-button-danger" style={{ fontSize: '0.7rem' }} onClick={handleReject} loading={processing} />
                    </div>
                }
            >
                <div className="field">
                    <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Rejection Reason <span className="text-red-500">*</span></label>
                    <Dropdown
                        value={rejectData.reason}
                        options={REJECTION_REASONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => setRejectData({ ...rejectData, reason: e.value })}
                        placeholder="Select reason"
                        className="w-full"
                        style={{ fontSize: '0.7rem' }}
                    />
                </div>
                <div className="field">
                    <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Additional Notes</label>
                    <InputTextarea
                        value={rejectData.notes}
                        onChange={(e) => setRejectData({ ...rejectData, notes: e.target.value })}
                        rows={2}
                        placeholder="Optional notes..."
                        className="w-full"
                        style={{ fontSize: '0.7rem' }}
                    />
                </div>
            </Dialog>

            {/* Clarification Request Dialog */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-question-circle text-orange-500" style={{ fontSize: '0.8rem' }} />
                        <span style={{ fontSize: '0.85rem' }}>Request Clarification</span>
                    </div>
                }
                visible={showClarificationDialog}
                onHide={() => setShowClarificationDialog(false)}
                style={{ width: '450px' }}
                modal
                className="compact-conversion-clarify-dialog"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text" style={{ fontSize: '0.7rem' }} onClick={() => setShowClarificationDialog(false)} />
                        <Button label="Send Request" style={{ fontSize: '0.7rem' }} onClick={handleRequestClarification} loading={processing} />
                    </div>
                }
            >
                <div className="field">
                    <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>What clarification is needed?</label>
                    <InputTextarea
                        value={clarificationNotes}
                        onChange={(e) => setClarificationNotes(e.target.value)}
                        rows={3}
                        placeholder="Describe what additional information or corrections you need..."
                        className="w-full"
                        style={{ fontSize: '0.7rem' }}
                    />
                </div>
            </Dialog>

            {/* Clarification Response Dialog */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-reply text-blue-500" style={{ fontSize: '0.8rem' }} />
                        <span style={{ fontSize: '0.85rem' }}>Respond to Clarification</span>
                    </div>
                }
                visible={showRespondDialog}
                onHide={() => setShowRespondDialog(false)}
                style={{ width: '450px' }}
                modal
                className="compact-conversion-respond-dialog"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text" style={{ fontSize: '0.7rem' }} onClick={() => setShowRespondDialog(false)} />
                        <Button label="Submit Response" style={{ fontSize: '0.7rem' }} onClick={handleRespondToClarification} loading={processing} />
                    </div>
                }
            >
                {submission?.clarification?.notes && (
                    <div className="mb-3 p-2 surface-ground border-round">
                        <div className="font-semibold text-600 mb-1" style={{ fontSize: '0.7rem' }}>Owner's Request:</div>
                        <div className="text-900" style={{ fontSize: '0.7rem' }}>{submission.clarification.notes}</div>
                    </div>
                )}
                <div className="field">
                    <label className="font-medium block mb-1" style={{ fontSize: '0.75rem' }}>Your Response</label>
                    <InputTextarea
                        value={clarificationResponse}
                        onChange={(e) => setClarificationResponse(e.target.value)}
                        rows={3}
                        placeholder="Provide clarification or explanation..."
                        className="w-full"
                        style={{ fontSize: '0.7rem' }}
                    />
                </div>
            </Dialog>

            {/* Compact styling for conversion tab dialogs and inputs */}
            <style>{`
                .compact-conversion-reject-dialog .p-dialog-header,
                .compact-conversion-clarify-dialog .p-dialog-header,
                .compact-conversion-respond-dialog .p-dialog-header {
                    padding: 0.75rem 1rem !important;
                }
                .compact-conversion-reject-dialog .p-dialog-content,
                .compact-conversion-clarify-dialog .p-dialog-content,
                .compact-conversion-respond-dialog .p-dialog-content {
                    padding: 0.5rem 1rem !important;
                }
                .compact-conversion-reject-dialog .p-dialog-footer,
                .compact-conversion-clarify-dialog .p-dialog-footer,
                .compact-conversion-respond-dialog .p-dialog-footer {
                    padding: 0.5rem 1rem !important;
                }
                .compact-conversion-reject-dialog .p-dropdown .p-dropdown-label,
                .compact-conversion-reject-dialog .p-dropdown .p-placeholder {
                    font-size: 0.7rem !important;
                    padding: 0.4rem 0.5rem !important;
                }
                .compact-conversion-reject-dialog .p-inputtextarea,
                .compact-conversion-clarify-dialog .p-inputtextarea,
                .compact-conversion-respond-dialog .p-inputtextarea {
                    font-size: 0.7rem !important;
                }
            `}</style>
        </div>
    );
};

export default ConversionTab;
